// useLeadsInbox — single source of truth for the artist inbox UI.
//
// Loads:
//   - Direct leads addressed to the signed-in artist's shop.
//   - Pool quote requests the artist can see (RLS gates this:
//     entitled-and-accepting-pool artists only, unexpired only).
//   - The artist's own pool_request_views rows — per-pool-request status
//     (separate table because pool requests are broadcast 1-to-many).
//
// Subscribes to Supabase realtime so new leads, status changes, and view
// inserts propagate without manual refresh.
//
// Returns:
//   loading       — initial load flag (true until first fetch resolves)
//   error         — last load/mutation error string, or null
//   shopId        — the artist's shop ID, or null if they don't have one
//   shop          — the artist's shop row (display_name, name, email,
//                   slug…) or null. Used by the detail view for "from"
//                   labels in mailto: and booking confirmation emails.
//   direct        — array of direct leads, sorted newest first
//   pool          — array of { request, view } where view may be null
//                   ("not yet viewed by this artist")
//   unreadDirect  — count of direct leads with status === 'new'
//   unreadPool    — count of pool requests with no matching view row
//   refresh()     — force a full reload from the server
//
// Mutations (all optimistic with reload-from-server rollback on error):
//   markLeadViewed(leadId)
//     — idempotent. Stamps viewed_at + bumps status to 'viewed' only if
//       currently 'new'. Called auto-on-mount in the detail view.
//   markLeadStatus(leadId, newStatus, { responded? })
//     — full lifecycle setter for direct leads. opts.responded=true also
//       stamps responded_at if not already set (used by "Reply via email").
//   savePoolView(quoteRequestId)
//     — idempotent. Inserts a pool_request_views row with status='viewed'
//       on first read of a pool request.
//   savePoolStatus(quoteRequestId, newStatus, { responded? })
//     — upserts the view row with the new per-artist status. opts.responded
//       same semantics as markLeadStatus.
//   saveLeadNotes(leadId, notes)
//     — sets leads.artist_notes. Optimistic, rolls back on error. Caller
//       is responsible for debouncing — this hits the DB on every call.
//   savePoolNotes(quoteRequestId, notes)
//     — same for pool requests; upserts the view row if missing so notes
//       can be jotted before the artist takes any other action.
//   sendBookingConfirmation(leadId)
//     — fires the lead-booking-confirmation Edge Function. Used by the
//       Booked confirmation modal in Phase 4 with the customer-email
//       checkbox left checked. Pure side-effect (sends an email); the
//       caller is still responsible for flipping status to 'booked'.
//   markAllDirectViewed()
//     — bulk-flip every direct lead with status='new' to 'viewed' for the
//       artist's shop. Single UPDATE … WHERE shop_id=? AND status='new'.
//       Returns the count of rows that *were* unread (so the caller can
//       toast). No-op if there are no unreads.
//   markAllPoolViewed()
//     — bulk-create pool_request_views rows (status='viewed') for every
//       pool entry without an existing view. Uses upsert with
//       ignoreDuplicates so concurrent inserts are safe. Returns the count.
//
// Mutation pattern: helpers throw on error AFTER triggering the rollback
// so callers can surface the failure (toast, etc.) without also being
// responsible for restoring state.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../../auth/AuthProvider";

export function useLeadsInbox() {
  const { session, loading: authLoading } = useAuth();
  const userId = session?.user?.id ?? null;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shopId, setShopId] = useState(null);
  const [shop, setShop] = useState(null);
  const [direct, setDirect] = useState([]);
  // pool: [{ request: quote_requests row, view: pool_request_views row | null }]
  const [pool, setPool] = useState([]);
  const mountedRef = useRef(true);

  // ---------------------------------------------------------------------------
  // Load — full refetch.
  //
  // Two-stage: (1) get the artist's shop_id, then (2) parallel fetch leads,
  // pool requests, and the artist's pool views. The leads/pool-views queries
  // both need shop_id, so they have to wait for stage 1.
  // ---------------------------------------------------------------------------
  const load = useCallback(async () => {
    if (!userId) {
      setShopId(null);
      setShop(null);
      setDirect([]);
      setPool([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // (1) Find this artist's shop. RLS only ever returns their own row.
      // We pull the fields the detail view needs so it doesn't have to
      // re-fetch (display name for "from" labels, email for replies, slug
      // for the public profile link).
      const { data: shopRow, error: shopErr } = await supabase
        .from("shops")
        .select("id, owner_user_id, name, display_name, email, slug")
        .eq("owner_user_id", userId)
        .maybeSingle();

      if (shopErr) throw shopErr;
      const myShopId = shopRow?.id ?? null;

      if (!mountedRef.current) return;
      setShopId(myShopId);
      setShop(shopRow ?? null);

      if (!myShopId) {
        // No shop yet — render an empty inbox. Phase 5's nav gate will
        // hide the entry entirely for these users, but the hook stays
        // safe to call.
        setDirect([]);
        setPool([]);
        setLoading(false);
        return;
      }

      // (2) Parallel fetch. RLS handles row-level filtering on all three.
      const [leadsRes, poolRes, viewsRes] = await Promise.all([
        supabase
          .from("leads")
          .select("*")
          .eq("shop_id", myShopId)
          .order("created_at", { ascending: false }),
        supabase
          .from("quote_requests")
          .select("*")
          .order("created_at", { ascending: false }),
        supabase
          .from("pool_request_views")
          .select("*")
          .eq("shop_id", myShopId),
      ]);

      if (leadsRes.error) throw leadsRes.error;
      if (poolRes.error) throw poolRes.error;
      if (viewsRes.error) throw viewsRes.error;

      // Left-join pool requests with our view rows in JS. Doing this in
      // SQL would need an RPC (RLS doesn't compose across tables nicely
      // for the "left join optional row" shape we want).
      const viewsByReqId = new Map();
      for (const v of viewsRes.data ?? []) {
        viewsByReqId.set(v.quote_request_id, v);
      }
      const merged = (poolRes.data ?? []).map((req) => ({
        request: req,
        view: viewsByReqId.get(req.id) ?? null,
      }));

      if (!mountedRef.current) return;
      setDirect(leadsRes.data ?? []);
      setPool(merged);
      setLoading(false);
    } catch (err) {
      if (!mountedRef.current) return;
      console.warn("[useLeadsInbox] load error", err);
      setError(err?.message || String(err));
      setLoading(false);
    }
  }, [userId]);

  // Initial load + refire whenever auth resolves to a different user.
  useEffect(() => {
    mountedRef.current = true;
    if (authLoading) return;
    load();
    return () => {
      mountedRef.current = false;
    };
  }, [authLoading, load]);

  // ---------------------------------------------------------------------------
  // Realtime — refetch on any change. v1 uses brute-force reload because
  // the data set is small and incremental merges are easy to get subtly
  // wrong (row arrives, status flips, deletion, etc.). If reload becomes
  // a perf issue, switch to applying the payload directly.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!userId || !shopId) return;
    const channel = supabase
      .channel(`leads-inbox-${shopId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "leads",
          filter: `shop_id=eq.${shopId}`,
        },
        () => load()
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "quote_requests",
          // No filter — pool requests are broadcast; RLS filters reads.
        },
        () => load()
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "pool_request_views",
          filter: `shop_id=eq.${shopId}`,
        },
        () => load()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, shopId, load]);

  // ---------------------------------------------------------------------------
  // Derived counts — used by the badge + tab labels.
  // ---------------------------------------------------------------------------
  const unreadDirect = useMemo(
    () => direct.filter((l) => l.status === "new").length,
    [direct]
  );
  const unreadPool = useMemo(
    () => pool.filter(({ view }) => !view).length,
    [pool]
  );

  // ---------------------------------------------------------------------------
  // Mutations
  //
  // All four follow the same shape:
  //   1. Apply optimistic update to local state.
  //   2. Hit the DB.
  //   3. On error: console.warn, setError, call load() to rollback to
  //      authoritative server state, then THROW so the caller can react.
  // ---------------------------------------------------------------------------

  const markLeadViewed = useCallback(
    async (leadId) => {
      // Read current state via setState callback so the function doesn't
      // need `direct` in deps (and stays stable across re-renders).
      let alreadyViewed = false;
      const stamp = new Date().toISOString();

      setDirect((prev) => {
        const target = prev.find((l) => l.id === leadId);
        if (!target) {
          alreadyViewed = true; // unknown lead — treat as no-op
          return prev;
        }
        if (target.status !== "new") {
          alreadyViewed = true;
          return prev;
        }
        return prev.map((l) =>
          l.id === leadId ? { ...l, status: "viewed", viewed_at: stamp } : l
        );
      });

      if (alreadyViewed) return;

      // Race-safe update: only flip if still 'new' on the server.
      const { error: err } = await supabase
        .from("leads")
        .update({ status: "viewed", viewed_at: stamp })
        .eq("id", leadId)
        .eq("status", "new");

      if (err) {
        console.warn("[useLeadsInbox] markLeadViewed error", err);
        setError(err.message || String(err));
        load();
        throw err;
      }
    },
    [load]
  );

  const markLeadStatus = useCallback(
    async (leadId, newStatus, opts = {}) => {
      const stamp = new Date().toISOString();
      let didFind = false;
      let patch = { status: newStatus };

      setDirect((prev) => {
        const target = prev.find((l) => l.id === leadId);
        if (!target) return prev;
        didFind = true;
        if (opts.responded && !target.responded_at) {
          patch = { ...patch, responded_at: stamp };
        }
        return prev.map((l) => (l.id === leadId ? { ...l, ...patch } : l));
      });

      if (!didFind) return;

      const { error: err } = await supabase
        .from("leads")
        .update(patch)
        .eq("id", leadId);

      if (err) {
        console.warn("[useLeadsInbox] markLeadStatus error", err);
        setError(err.message || String(err));
        load();
        throw err;
      }
    },
    [load]
  );

  const savePoolView = useCallback(
    async (quoteRequestId) => {
      if (!shopId) return;
      const stamp = new Date().toISOString();
      let alreadyViewed = false;

      setPool((prev) => {
        const idx = prev.findIndex((p) => p.request.id === quoteRequestId);
        if (idx === -1) return prev; // unknown request, but still try server
        if (prev[idx].view) {
          alreadyViewed = true;
          return prev;
        }
        const next = prev.slice();
        next[idx] = {
          ...prev[idx],
          view: {
            id: `optimistic-${quoteRequestId}`,
            shop_id: shopId,
            quote_request_id: quoteRequestId,
            status: "viewed",
            viewed_at: stamp,
            responded_at: null,
            artist_notes: null,
          },
        };
        return next;
      });

      if (alreadyViewed) return;

      const { error: err } = await supabase
        .from("pool_request_views")
        .upsert(
          {
            shop_id: shopId,
            quote_request_id: quoteRequestId,
            status: "viewed",
          },
          { onConflict: "shop_id,quote_request_id", ignoreDuplicates: true }
        );

      if (err) {
        console.warn("[useLeadsInbox] savePoolView error", err);
        setError(err.message || String(err));
        load();
        throw err;
      }
    },
    [shopId, load]
  );

  const savePoolStatus = useCallback(
    async (quoteRequestId, newStatus, opts = {}) => {
      if (!shopId) return;
      const stamp = new Date().toISOString();

      // We don't read pool inside the callback; collect what we need from
      // setState's prev arg.
      let respondedStamp = null;

      setPool((prev) => {
        const idx = prev.findIndex((p) => p.request.id === quoteRequestId);
        if (idx === -1) return prev;
        const existing = prev[idx].view;
        if (opts.responded && !existing?.responded_at) {
          respondedStamp = stamp;
        }
        const next = prev.slice();
        next[idx] = {
          ...prev[idx],
          view: {
            id: existing?.id ?? `optimistic-${quoteRequestId}`,
            shop_id: shopId,
            quote_request_id: quoteRequestId,
            status: newStatus,
            viewed_at: existing?.viewed_at ?? stamp,
            responded_at: existing?.responded_at ?? respondedStamp,
            artist_notes: existing?.artist_notes ?? null,
          },
        };
        return next;
      });

      const patch = {
        shop_id: shopId,
        quote_request_id: quoteRequestId,
        status: newStatus,
      };
      if (respondedStamp) patch.responded_at = respondedStamp;

      const { error: err } = await supabase
        .from("pool_request_views")
        .upsert(patch, { onConflict: "shop_id,quote_request_id" });

      if (err) {
        console.warn("[useLeadsInbox] savePoolStatus error", err);
        setError(err.message || String(err));
        load();
        throw err;
      }
    },
    [shopId, load]
  );

  // saveLeadNotes — write the artist's private notes for a direct lead.
  // Caller debounces; we just commit. Optimistic with rollback on error.
  const saveLeadNotes = useCallback(
    async (leadId, notes) => {
      let didFind = false;
      const value = notes ?? "";

      setDirect((prev) => {
        const target = prev.find((l) => l.id === leadId);
        if (!target) return prev;
        didFind = true;
        return prev.map((l) =>
          l.id === leadId ? { ...l, artist_notes: value } : l
        );
      });

      if (!didFind) return;

      const { error: err } = await supabase
        .from("leads")
        .update({ artist_notes: value })
        .eq("id", leadId);

      if (err) {
        console.warn("[useLeadsInbox] saveLeadNotes error", err);
        setError(err.message || String(err));
        load();
        throw err;
      }
    },
    [load]
  );

  // savePoolNotes — write the artist's private notes for a pool request.
  // If no view row exists yet, upsert creates one with status='viewed'
  // (so jotting notes implicitly opens the request). Optimistic + rollback.
  const savePoolNotes = useCallback(
    async (quoteRequestId, notes) => {
      if (!shopId) return;
      const stamp = new Date().toISOString();
      const value = notes ?? "";

      setPool((prev) => {
        const idx = prev.findIndex((p) => p.request.id === quoteRequestId);
        if (idx === -1) return prev;
        const existing = prev[idx].view;
        const next = prev.slice();
        next[idx] = {
          ...prev[idx],
          view: {
            id: existing?.id ?? `optimistic-${quoteRequestId}`,
            shop_id: shopId,
            quote_request_id: quoteRequestId,
            status: existing?.status ?? "viewed",
            viewed_at: existing?.viewed_at ?? stamp,
            responded_at: existing?.responded_at ?? null,
            artist_notes: value,
          },
        };
        return next;
      });

      const { error: err } = await supabase
        .from("pool_request_views")
        .upsert(
          {
            shop_id: shopId,
            quote_request_id: quoteRequestId,
            artist_notes: value,
            // If we're inserting a new row, default it to 'viewed'.
            status: "viewed",
          },
          { onConflict: "shop_id,quote_request_id" }
        );

      if (err) {
        console.warn("[useLeadsInbox] savePoolNotes error", err);
        setError(err.message || String(err));
        load();
        throw err;
      }
    },
    [shopId, load]
  );

  // markAllDirectViewed — bulk flip every status='new' direct lead to
  // 'viewed' in one round-trip. Optimistic with rollback. Returns the
  // count that was actually unread (for toast copy). No-op when zero.
  const markAllDirectViewed = useCallback(async () => {
    if (!shopId) return 0;
    const stamp = new Date().toISOString();
    let count = 0;

    setDirect((prev) => {
      const unread = prev.filter((l) => l.status === "new");
      count = unread.length;
      if (!count) return prev;
      return prev.map((l) =>
        l.status === "new" ? { ...l, status: "viewed", viewed_at: stamp } : l
      );
    });

    if (!count) return 0;

    // Single UPDATE — RLS scopes to this shop, plus the explicit shop_id
    // belt-and-suspenders so a future RLS regression can't leak across
    // shops, and the status='new' guard makes this race-safe against an
    // artist also opening a row in another tab.
    const { error: err } = await supabase
      .from("leads")
      .update({ status: "viewed", viewed_at: stamp })
      .eq("shop_id", shopId)
      .eq("status", "new");

    if (err) {
      console.warn("[useLeadsInbox] markAllDirectViewed error", err);
      setError(err.message || String(err));
      load();
      throw err;
    }

    return count;
  }, [shopId, load]);

  // markAllPoolViewed — for every pool entry without an existing view row,
  // upsert a view with status='viewed'. The pool table has no
  // shop-scoped UPDATE shape (each pool entry needs its own row), so we
  // batch them into a single upsert.
  const markAllPoolViewed = useCallback(async () => {
    if (!shopId) return 0;
    const stamp = new Date().toISOString();
    let toInsert = [];

    setPool((prev) => {
      const unread = prev.filter(({ view }) => !view);
      toInsert = unread.map(({ request }) => ({
        shop_id: shopId,
        quote_request_id: request.id,
        status: "viewed",
      }));
      if (!toInsert.length) return prev;
      return prev.map((p) =>
        p.view
          ? p
          : {
              ...p,
              view: {
                id: `optimistic-${p.request.id}`,
                shop_id: shopId,
                quote_request_id: p.request.id,
                status: "viewed",
                viewed_at: stamp,
                responded_at: null,
                artist_notes: null,
              },
            }
      );
    });

    if (!toInsert.length) return 0;

    const { error: err } = await supabase
      .from("pool_request_views")
      .upsert(toInsert, {
        onConflict: "shop_id,quote_request_id",
        ignoreDuplicates: true,
      });

    if (err) {
      console.warn("[useLeadsInbox] markAllPoolViewed error", err);
      setError(err.message || String(err));
      load();
      throw err;
    }

    return toInsert.length;
  }, [shopId, load]);

  // sendBookingConfirmation — fire the lead-booking-confirmation Edge
  // Function. Pure side-effect: sends an email to the customer letting
  // them know the artist has accepted the booking. The caller still owns
  // flipping the lead to status='booked' (split so the modal can opt out
  // of the email without changing the status flow).
  const sendBookingConfirmation = useCallback(async (leadId) => {
    const { data, error: err } = await supabase.functions.invoke(
      "lead-booking-confirmation",
      { body: { leadId } }
    );
    if (err) {
      console.warn("[useLeadsInbox] sendBookingConfirmation error", err);
      // Don't setError here — the surrounding modal will toast its own
      // failure. Sending the email failing should NOT poison the inbox
      // load state.
      throw err;
    }
    return data;
  }, []);

  return {
    loading: loading || authLoading,
    error,
    shopId,
    shop,
    direct,
    pool,
    unreadDirect,
    unreadPool,
    refresh: load,
    markLeadViewed,
    markLeadStatus,
    savePoolView,
    savePoolStatus,
    saveLeadNotes,
    savePoolNotes,
    sendBookingConfirmation,
    markAllDirectViewed,
    markAllPoolViewed,
  };
}
